// src/components/AdminSupplierList.js
import React, { useEffect, useMemo, useState } from "react";
import API from "../services/api";
import { useNavigate } from "react-router-dom";
import {
  Row, Col, Form, Button, Badge, Spinner, Alert,
  Offcanvas, Modal
} from "react-bootstrap";
import "./AdminVendorList.css";

/* ===================== small utils ===================== */
const fmtIST = (d) =>
  d ? new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—";
const orDash = (v) =>
  v === 0 ? "0" : (v == null || String(v).trim() === "" ? "—" : String(v));
const tel = (x) => (x ? <a href={`tel:${x}`}>{x}</a> : "—");
const mail = (x) => (x ? <a href={`mailto:${x}`}>{x}</a> : "—");
const toCSV = (rows) =>
  "\ufeff" + rows.map(r => r.map(x => `"${String(x ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const getPoRemainingQty = (po = {}) => {
  const remaining = Number(po?.remainingQty ?? po?.remainingQtyStored);
  if (Number.isFinite(remaining)) return remaining;
  const total = Number(po?.totalQty);
  return Number.isFinite(total) ? total : 0;
};

const isPoOpen = (po = {}) => Boolean(po?.poNumber) && getPoRemainingQty(po) > 0;

const buildPoForm = (source = {}, fallback = {}) => ({
  poNumber: source.poNumber || fallback.poNumber || "",
  batchNumber: source.batchNumber || fallback.batchNumber || "",
  productName: source.productName || fallback.productName || "",
  unit: source.unit || fallback.unit || "ton",
  totalQty: source.totalQty ?? fallback.totalQty ?? "",
  pricePerUnit: source.pricePerUnit ?? fallback.pricePerUnit ?? "",
  paidAmount: source.paidAmount ?? fallback.paidAmount ?? "",
});

const poLabel = (po = {}) => {
  const remaining = getPoRemainingQty(po);
  return [
    po.poNumber || "No PO",
    po.productName || "No product",
    po.batchNumber || "No batch",
    `Rem: ${Number.isFinite(remaining) ? remaining : "—"} ${po.unit || "ton"}`,
  ].join(" | ");
};

const getInitials = (name = "") =>
  String(name || "")
    .split(" ")
    .map((part) => part.trim().charAt(0))
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "SM";

const getAvatarTone = (seed = "") => {
  const tones = ["blue", "green", "amber", "violet", "rose", "sky"];
  const total = String(seed || "").split("").reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  return tones[total % tones.length];
};

/* ===================== Normalize row ===================== */
const toViewModel = (raw) => {
  if (!raw) return null;
  const p = raw.profile || {};

  return {
    _id: String(raw._id || ""),
    fullName: raw.fullName || "",
    email: raw.email || "",
    mobile: raw.mobile || "",
    supplierName: p.supplierName || raw.fullName || "",
    companyName: p.companyName || "",
    supplierCode: p.supplierCode || "",
    gstNumber: p.gstNumber || "",
    panNumber: p.panNumber || "",
    addressText: p.address || "",
    contactPerson: p.contactPerson || "",
    contactMobile: p.contactMobile || "",
    status: p.status || "active",
    approved: typeof raw.approved === "boolean" ? raw.approved : false,
    certificateStatus: raw.certificateStatus || "pending",
    createdAt: raw.createdAt || null,
    updatedAt: raw.updatedAt || null,
    po: p.po || null,
  };
};

/* ===================== UI bits ===================== */
const StatusPills = ({ approved, certificateStatus, status }) => (
  <div className="d-flex flex-column gap-1">
    <Badge bg={approved ? "success" : "secondary"}>{approved ? "Approved" : "Pending"}</Badge>
    <Badge bg={certificateStatus === "generated" ? "info" : "warning"} className="text-dark">
      Cert: {certificateStatus || "pending"}
    </Badge>
    <Badge bg={status === "inactive" ? "secondary" : "success"}>Status: {status || "active"}</Badge>
  </div>
);

const EmptyState = ({ onClear }) => (
  <div className="text-center text-muted py-5 border rounded">
    <div style={{ fontSize: 48, lineHeight: 1 }}>🗂️</div>
    <div className="mt-2">No suppliers found.</div>
    {onClear && <Button className="mt-3" variant="outline-secondary" onClick={onClear}>Clear filters</Button>}
  </div>
);

/* ===================== Main Component ===================== */
const AdminSupplierList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [status, setStatus] = useState("all"); // all|approved|pending
  const [sortKey, setSortKey] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [showFilters, setShowFilters] = useState(false);

  const [showPoModal, setShowPoModal] = useState(false);
  const [poSupplier, setPoSupplier] = useState(null);
  const [poHistory, setPoHistory] = useState([]);
  const [supplierProducts, setSupplierProducts] = useState([]);
  const [poForm, setPoForm] = useState({
    poNumber: "",
    batchNumber: "",
    productName: "",
    unit: "ton",
    totalQty: "",
    pricePerUnit: "",
    paidAmount: "",
  });
  const [poHints, setPoHints] = useState({
    suggestedPoNumber: "",
    suggestedBatchNumber: "",
  });
  const [poMode, setPoMode] = useState("new");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 200);
    return () => clearTimeout(t);
  }, [search]);

  const fetchSuppliers = async () => {
    setLoading(true);
    setNote(null);
    try {
      const res = await API.get("/admin/suppliers");
      const list = Array.isArray(res.data) ? res.data : (res.data?.suppliers || []);
      const mapped = list.map(toViewModel).filter(Boolean);
      setRows(mapped);
    } catch (err) {
      const code = err?.response?.status;
      setNote({ variant: "danger", text: `Failed to load suppliers${code ? ` (${code})` : ""}` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSuppliers(); }, []);

  const clearFilters = () => { setSearch(""); setStatus("all"); };

  const filtered = useMemo(() => {
    const q = debounced.toLowerCase();
    return rows.filter((v) => {
      const okStatus =
        status === "all" ? true :
        status === "approved" ? v.approved :
        status === "pending" ? !v.approved : true;

      if (!okStatus) return false;
      if (!q) return true;

      const blob = [
        v.supplierName, v.companyName, v.gstNumber, v.panNumber,
        v.addressText, v.mobile, v.contactMobile, v.supplierCode
      ].filter(Boolean).join(" | ").toLowerCase();

      return blob.includes(q);
    });
  }, [rows, debounced, status]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1 * dir;
      if (bv == null) return -1 * dir;
      if (av instanceof Date && bv instanceof Date) return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return list;
  }, [filtered, sortKey, sortDir]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(sorted.length / pageSize)), [sorted, pageSize]);
  const statusCounts = useMemo(
    () => ({
      all: rows.length,
      approved: rows.filter((row) => row.approved).length,
      pending: rows.filter((row) => !row.approved).length,
    }),
    [rows]
  );

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      await API.put(`/admin/suppliers/${id}/approve`);
      setNote({ variant: "success", text: "Supplier approved" });
      await fetchSuppliers();
    } catch (e) {
      setNote({ variant: "danger", text: "Approve failed" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id) => {
    setActionLoading(id);
    try {
      await API.put(`/admin/suppliers/${id}/reject`);
      setNote({ variant: "success", text: "Supplier rejected" });
      await fetchSuppliers();
    } catch (e) {
      setNote({ variant: "danger", text: "Reject failed" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this supplier? This action cannot be undone.")) return;
    setActionLoading(id);
    try {
      await API.delete(`/admin/suppliers/${id}`);
      setNote({ variant: "success", text: "Supplier deleted" });
      await fetchSuppliers();
    } catch (e) {
      setNote({ variant: "danger", text: "Delete failed" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendWelcome = async (id) => {
    setActionLoading(id);
    try {
      const res = await API.post(`/admin/whatsapp/send-registration-welcome/${id}`);
      setNote({ variant: "success", text: res.data?.message || "Welcome WhatsApp sent" });
    } catch (e) {
      setNote({ variant: "danger", text: e?.response?.data?.message || "Failed to send welcome WhatsApp" });
    } finally {
      setActionLoading(null);
    }
  };

  const fetchSupplierProducts = async () => {
    try {
      const res = await API.get("/admin/products");
      const products = Array.isArray(res.data) ? res.data : [];
      setSupplierProducts(
        products
          .filter((product) => product?.active !== false && product?.targetAudience === "supplier")
          .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
      );
    } catch (_err) {
      setSupplierProducts([]);
    }
  };

  const openPoModal = async (supplier) => {
    setPoSupplier(supplier);
    setPoHistory([]);
    fetchSupplierProducts();
    setPoMode("new");
    setPoHints({ suggestedPoNumber: "", suggestedBatchNumber: "" });
    setPoForm(buildPoForm({}));
    setShowPoModal(true);

    try {
      const res = await API.get(`/admin/suppliers/${supplier._id}/po-details`);
      const po = res.data?.po || {};
      const history = Array.isArray(res.data?.poHistory) ? res.data.poHistory : [];
      const running = history.filter(isPoOpen);
      const latestPo = history.length > 0 ? history[0] : po;
      const selectedPo = running[0] || latestPo || po;
      setPoHistory(history);
      setPoSupplier((prev) => (prev ? { ...prev, po: selectedPo } : prev));
      setPoHints({
        suggestedPoNumber: po.suggestedPoNumber || "",
        suggestedBatchNumber: po.suggestedBatchNumber || "",
      });
      setPoForm(
        buildPoForm(
          {
            poNumber: po.suggestedPoNumber || "",
            batchNumber: po.suggestedBatchNumber || "",
            unit: latestPo?.unit || po.unit || "ton",
          },
          {}
        )
      );
    } catch (_err) {
      // keep modal usable with current supplier data even if preview fetch fails
    }
  };

  const savePo = async () => {
    if (!poSupplier?._id) return;
    setActionLoading(poSupplier._id);
    try {
      await API.put(`/admin/suppliers/${poSupplier._id}/po`, {
        poNumber: poForm.poNumber,
        batchNumber: poForm.batchNumber,
        productName: poForm.productName,
        unit: poForm.unit,
        totalQty: poForm.totalQty,
        pricePerUnit: poForm.pricePerUnit,
        paidAmount: poForm.paidAmount,
        currentPoNumber: poMode === "edit" ? poForm.poNumber || poSupplier?.po?.poNumber || "" : "",
      });
      setNote({ variant: "success", text: poMode === "edit" ? "PO updated" : "New PO created" });
      setShowPoModal(false);
      await fetchSuppliers();
    } catch (e) {
      setNote({ variant: "danger", text: e?.response?.data?.message || "Failed to update PO" });
    } finally {
      setActionLoading(null);
    }
  };

  const viewHref = (id) => `/admin/suppliers/${id}/account`;
  const runningPoOptions = useMemo(() => poHistory.filter(isPoOpen), [poHistory]);
  const selectedEditPo = useMemo(
    () =>
      runningPoOptions.find((po) => String(po.poNumber || "") === String(poForm.poNumber || "")) ||
      poSupplier?.po ||
      null,
    [runningPoOptions, poForm.poNumber, poSupplier]
  );
  const selectedProductMissing = Boolean(
    poForm.productName &&
      !supplierProducts.some((product) => String(product.name || "") === String(poForm.productName || ""))
  );
  const poTotalAmount = useMemo(() => {
    const qty = toNumber(poForm.totalQty);
    const rate = toNumber(poForm.pricePerUnit);
    const paid = toNumber(poForm.paidAmount);
    if (qty <= 0 || rate <= 0) return 0;
    return Math.max(0, (qty * rate) - paid);
  }, [poForm.totalQty, poForm.pricePerUnit, poForm.paidAmount]);

  const exportCSV = () => {
    const rowsCSV = sorted.map((v) => [
      v.supplierName,
      v.companyName,
      v.supplierCode,
      v.gstNumber,
      v.panNumber,
      v.contactPerson,
      v.contactMobile,
      v.status,
      v.approved ? "Approved" : "Pending",
      v.certificateStatus,
      v.createdAt ? new Date(v.createdAt).toISOString() : "",
    ]);

    const header = [
      "Supplier Name",
      "Company Name",
      "Supplier Code",
      "GST",
      "PAN",
      "Contact Person",
      "Contact Mobile",
      "Status",
      "Approval",
      "Certificate Status",
      "Created At",
    ];

    const csv = toCSV([header, ...rowsCSV]);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "suppliers.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="vm-page">
      <div className="vm-container">
        <div className="vm-topbar">
          <div className="vm-title-wrap">
            <div className="vm-logo">SM</div>
            <div>
              <div className="vm-title">Supplier Management</div>
              <div className="vm-subtitle vm-subtitle-normal">Manage and monitor all suppliers</div>
            </div>
          </div>
          <div className="vm-search vm-search-top d-none d-md-block">
            <span className="material-symbols-outlined">search</span>
            <input
              type="text"
              placeholder="Search suppliers by name, company, GST, mobile..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="vm-actions">
            <button className="vm-toolbar-btn vm-toolbar-btn-primary" onClick={exportCSV}>
              <span className="material-symbols-outlined">download</span>
              <span>Export CSV</span>
            </button>
            <button className="vm-toolbar-btn" onClick={fetchSuppliers}>
              <span className="material-symbols-outlined">refresh</span>
              <span>Refresh</span>
            </button>
            <Button className="d-md-none vm-filters-btn" variant="secondary" size="sm" onClick={() => setShowFilters(true)}>
              Filters
            </Button>
            <button className="vm-toolbar-btn vm-toolbar-btn-primary d-none d-md-inline-flex" onClick={() => setShowFilters(true)}>
              <span className="material-symbols-outlined">filter_list</span>
              <span>Filters</span>
            </button>
          </div>
        </div>

        <div className="vm-toolbar">
          <div>
            <div className="vm-tabs">
              <button className={`vm-tab ${status === "all" ? "active" : ""}`} onClick={() => setStatus("all")}>
                <span>All Suppliers</span>
                <span className="vm-tab-badge blue">{statusCounts.all}</span>
              </button>
              <button className={`vm-tab ${status === "approved" ? "active" : ""}`} onClick={() => setStatus("approved")}>
                <span>Approved</span>
                <span className="vm-tab-badge green">{statusCounts.approved}</span>
              </button>
              <button className={`vm-tab ${status === "pending" ? "active" : ""}`} onClick={() => setStatus("pending")}>
                <span>Pending</span>
                <span className="vm-tab-badge amber">{statusCounts.pending}</span>
              </button>
            </div>
          </div>

          <div className="vm-filter-row">
            <div className="vm-filter-stack">
              <div className="d-flex gap-2">
                <div className="vm-sort-label">Sort by</div>
                <Form.Select
                  size="sm"
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                >
                  <option value="createdAt">Sort: Created</option>
                  <option value="supplierName">Sort: Name</option>
                  <option value="companyName">Sort: Company</option>
                  <option value="supplierCode">Sort: Code</option>
                </Form.Select>
                <Form.Select
                  size="sm"
                  value={sortDir}
                  onChange={(e) => setSortDir(e.target.value)}
                >
                  <option value="desc">Desc</option>
                  <option value="asc">Asc</option>
                </Form.Select>
              </div>
              <div className="vm-search d-md-none">
                <span className="material-symbols-outlined">search</span>
                <input
                  type="text"
                  placeholder="Search suppliers..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Filters (Mobile Offcanvas) */}
        <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="bottom">
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>Filters</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <Form.Group className="mb-3">
              <Form.Label>Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="name, company, GST, PAN, mobile..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="all">All</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Sort By</Form.Label>
              <Form.Select value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
                <option value="createdAt">Created Date</option>
                <option value="supplierName">Supplier Name</option>
                <option value="companyName">Company</option>
                <option value="supplierCode">Supplier Code</option>
              </Form.Select>
            </Form.Group>
            <div className="d-flex gap-2">
              <Button onClick={() => setShowFilters(false)}>Apply</Button>
              <Button variant="secondary" onClick={clearFilters}>Clear</Button>
            </div>
          </Offcanvas.Body>
        </Offcanvas>

        {note && <Alert variant={note.variant} onClose={() => setNote(null)} dismissible>{note.text}</Alert>}

        <div className="mt-3">
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : pageRows.length === 0 ? (
            <EmptyState onClear={clearFilters} />
          ) : (
            <Row xs={1} md={3} className="g-3">
              {pageRows.map(v => (
                <Col key={v._id}>
                  <div className="vm-entity-grid-card">
                    <div className="vm-entity-head">
                      <div className="vm-supplier-head">
                        <div className={`vm-avatar vm-avatar-${getAvatarTone(v._id || v.supplierName)}`}>
                          {getInitials(v.supplierName || v.companyName || v.fullName)}
                        </div>
                        <div className="vm-supplier-copy">
                          <div className="vm-person-name">{orDash(v.supplierName)}</div>
                          <div className="vm-company-name">{orDash(v.companyName)}</div>
                          <div className="vm-code-chip">{orDash(v.supplierCode)}</div>
                        </div>
                      </div>
                      <div className="vm-status-stack">
                        <StatusPills approved={v.approved} certificateStatus={v.certificateStatus} status={v.status} />
                      </div>
                    </div>
                    <div className="vm-kv-grid">
                      <div className="vm-kv">
                        <span className="vm-k"><span className="material-symbols-outlined">smartphone</span>Mobile</span>
                        <span className="vm-v">{tel(v.mobile || v.contactMobile)}</span>
                      </div>
                      <div className="vm-kv vm-kv-full">
                        <span className="vm-k"><span className="material-symbols-outlined">location_on</span>Address</span>
                        <span className="vm-v">{orDash(v.addressText)}</span>
                      </div>
                      <div className="vm-kv">
                        <span className="vm-k"><span className="material-symbols-outlined">mail</span>Email</span>
                        <span className="vm-v">{mail(v.email)}</span>
                      </div>
                      <div className="vm-kv">
                        <span className="vm-k"><span className="material-symbols-outlined">person</span>Contact Person</span>
                        <span className="vm-v">{orDash(v.contactPerson)}</span>
                      </div>
                      <div className="vm-kv">
                        <span className="vm-k"><span className="material-symbols-outlined">badge</span>GST Number</span>
                        <span className="vm-v vm-v-mono">{orDash(v.gstNumber)}</span>
                      </div>
                      <div className="vm-kv">
                        <span className="vm-k"><span className="material-symbols-outlined">call</span>Contact Mobile</span>
                        <span className="vm-v">{tel(v.contactMobile)}</span>
                      </div>
                      <div className="vm-kv">
                        <span className="vm-k"><span className="material-symbols-outlined">credit_card</span>PAN Number</span>
                        <span className="vm-v vm-v-mono">{orDash(v.panNumber)}</span>
                      </div>
                    </div>
                    <div className="vm-meta-row">
                      <div className="vm-meta-chip">
                        <span className="material-symbols-outlined">calendar_today</span>
                        <span>Created: {fmtIST(v.createdAt)}</span>
                      </div>
                      <div className="vm-meta-chip">
                        <span className="material-symbols-outlined">update</span>
                        <span>Updated: {fmtIST(v.updatedAt)}</span>
                      </div>
                    </div>
                    <div className="vm-po-grid">
                      <div className="vm-po-cell">
                        <span className="vm-k"><span className="material-symbols-outlined">description</span>PO Number</span>
                        <span className="vm-v vm-v-mono">{orDash(v.po?.poNumber)}</span>
                      </div>
                      <div className="vm-po-cell">
                        <span className="vm-k"><span className="material-symbols-outlined">sell</span>Batch Number</span>
                        <span className="vm-v vm-v-mono">{orDash(v.po?.batchNumber)}</span>
                      </div>
                    </div>
                    <div className="vm-actions-wrap">
                      <Button size="sm" className="vm-action-btn vm-action-blue" onClick={() => openPoModal(v)}>
                        <span className="material-symbols-outlined">note_add</span>
                        <span>Set PO</span>
                      </Button>
                      <Button size="sm" className="vm-action-btn vm-action-cyan" onClick={() => navigate(`/admin/suppliers/${v._id}/po`)}>
                        <span className="material-symbols-outlined">inventory</span>
                        <span>PO Details</span>
                      </Button>
                      <Button size="sm" className="vm-action-btn vm-action-neutral" onClick={() => navigate(viewHref(v._id))}>
                        <span className="material-symbols-outlined">badge</span>
                        <span>View Account</span>
                      </Button>
                      <Button size="sm" className="vm-action-btn vm-action-neutral" onClick={() => navigate(`/admin/suppliers/${v._id}/account?tab=ledger`)}>
                        <span className="material-symbols-outlined">history</span>
                        <span>View History</span>
                      </Button>
                      <Button size="sm" className="vm-action-btn vm-action-green" disabled={actionLoading === v._id} onClick={() => handleSendWelcome(v._id)}>
                        <span className="material-symbols-outlined">chat</span>
                        {actionLoading === v._id ? "Sending..." : "Send Welcome"}
                      </Button>
                      {v.approved ? (
                        <Button size="sm" className="vm-action-btn vm-action-amber" disabled={actionLoading === v._id} onClick={() => handleReject(v._id)}>
                          <span className="material-symbols-outlined">close</span>
                          {actionLoading === v._id ? "Rejecting..." : "Reject"}
                        </Button>
                      ) : (
                        <Button size="sm" className="vm-action-btn vm-action-success" disabled={actionLoading === v._id} onClick={() => handleApprove(v._id)}>
                          <span className="material-symbols-outlined">check</span>
                          {actionLoading === v._id ? "Approving..." : "Approve"}
                        </Button>
                      )}
                      <Button size="sm" className="vm-action-btn vm-action-danger-fill" disabled={actionLoading === v._id} onClick={() => handleDelete(v._id)}>
                        <span className="material-symbols-outlined">delete</span>
                        {actionLoading === v._id ? "Deleting..." : "Delete"}
                      </Button>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}

          {!loading && pageRows.length > 0 && (
            <div className="vm-pagination mt-3">
              <div className="small text-muted">
                Page {page} of {totalPages}
              </div>
              <div className="d-flex gap-2">
                <Form.Select size="sm" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={{ width: 110 }}>
                  {[10, 20, 50].map(s => <option key={s} value={s}>{s}/page</option>)}
                </Form.Select>
                <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
                  Prev
                </Button>
                <Button size="sm" variant="outline-secondary" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal show={showPoModal} onHide={() => setShowPoModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Set Supplier PO</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isPoOpen(poSupplier?.po) ? (
            <Alert variant="info" className="mb-3">
              Current PO me abhi <strong>{getPoRemainingQty(poSupplier?.po)} {poSupplier?.po?.unit || "ton"}</strong> quantity remaining hai.
              Aap naya PO bana sakte ho, lekin purana PO history se remove nahi hoga aur record me available rahega.
            </Alert>
          ) : null}
          {poSupplier?.po?.poNumber ? (
            <div className="small text-muted mb-2">
              Current PO: <strong>{poSupplier.po.poNumber}</strong>
            </div>
          ) : null}
          {poSupplier?.po?.batchNumber ? (
            <div className="small text-muted mb-2">
              Current Batch: <strong>{poSupplier.po.batchNumber}</strong>
            </div>
          ) : null}
          {poSupplier?.po?.productName ? (
            <div className="small text-muted mb-2">
              Current Product: <strong>{poSupplier.po.productName}</strong>
            </div>
          ) : null}
          <div className="d-flex gap-2 mb-3">
            <Button
              size="sm"
              variant={poMode === "new" ? "primary" : "outline-primary"}
              onClick={() => {
                setPoMode("new");
                setPoForm(buildPoForm({
                  poNumber: poHints.suggestedPoNumber || "",
                  batchNumber: poHints.suggestedBatchNumber || "",
                  unit: poSupplier?.po?.unit || "ton",
                }));
              }}
            >
              Create New PO
            </Button>
            <Button
              size="sm"
              variant={poMode === "edit" ? "dark" : "outline-dark"}
              disabled={runningPoOptions.length === 0}
              onClick={() => {
                const firstRunningPo = runningPoOptions[0] || poSupplier?.po || {};
                setPoMode("edit");
                setPoSupplier((prev) => (prev ? { ...prev, po: firstRunningPo } : prev));
                setPoForm(buildPoForm(firstRunningPo));
              }}
            >
              Edit Running PO
            </Button>
          </div>
          {poMode === "edit" ? (
            <Form.Group className="mb-3">
              <Form.Label>Select Running PO</Form.Label>
              <Form.Select
                value={poForm.poNumber}
                onChange={(e) => {
                  const selectedPo =
                    runningPoOptions.find((po) => String(po.poNumber || "") === String(e.target.value || "")) ||
                    {};
                  setPoSupplier((prev) => (prev ? { ...prev, po: selectedPo } : prev));
                  setPoForm(buildPoForm(selectedPo));
                }}
              >
                {runningPoOptions.map((po) => (
                  <option key={po.poNumber || po.batchNumber} value={po.poNumber || ""}>
                    {poLabel(po)}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Sirf running/open PO yahan dikh rahe hain. Select karke Product ya details update kar sakte ho.
              </Form.Text>
            </Form.Group>
          ) : null}
          <Form.Group className="mb-3">
            <Form.Label>PO Number</Form.Label>
            <Form.Control
              value={poForm.poNumber}
              onChange={(e) => setPoForm((p) => ({ ...p, poNumber: e.target.value }))}
              readOnly={poMode === "edit" && isPoOpen(selectedEditPo)}
              placeholder={poHints.suggestedPoNumber || "PO-LRM-2601-SUPPLIER-NAME"}
            />
            <Form.Text className="text-muted">
              {poMode === "new"
                ? "Naya PO banane ke liye suggested value prefilled hai. Chaho to change kar sakte ho."
                : isPoOpen(selectedEditPo)
                  ? "Selected running PO open hai, isliye edit mode me PO number locked hai. Product aur baaki details update kar sakte ho."
                  : "Selected PO edit mode me hai. Yahan same PO number rakho."}
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Batch Number</Form.Label>
            <Form.Control
              value={poForm.batchNumber}
              onChange={(e) => setPoForm((p) => ({ ...p, batchNumber: e.target.value }))}
              placeholder={poHints.suggestedBatchNumber || "LRM-2601-SUPPLIER-NAME-01"}
            />
            <Form.Text className="text-muted">
              Auto-generated batch bhi prefilled hai. Zarurat ho to manually update kar sakte ho.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Product</Form.Label>
            <Form.Select
              value={poForm.productName}
              onChange={(e) => {
                const product = supplierProducts.find((item) => item.name === e.target.value);
                setPoForm((p) => ({
                  ...p,
                  productName: product?.name || e.target.value,
                  unit: product?.unit ? String(product.unit).toLowerCase() : p.unit,
                }));
              }}
            >
              <option value="">Select supplier product</option>
              {selectedProductMissing ? (
                <option value={poForm.productName}>{poForm.productName}</option>
              ) : null}
              {supplierProducts.map((product) => (
                <option key={product._id || product.name} value={product.name}>
                  {product.name}{product.unit ? ` (${product.unit})` : ""}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Supplier catalog ke products yahan auto load hote hain. Product select karte hi PO us product ke against set hoga.
            </Form.Text>
            {supplierProducts.length === 0 ? (
              <div className="small text-danger mt-1">
                Supplier products nahi mile. Admin Products me target Supplier wala product add/active karo.
              </div>
            ) : null}
          </Form.Group>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Total Quantity</Form.Label>
                <Form.Control
                  type="number"
                  value={poForm.totalQty}
                  onChange={(e) => setPoForm((p) => ({ ...p, totalQty: e.target.value }))}
                  placeholder="5000"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Unit</Form.Label>
                <Form.Select
                  value={poForm.unit}
                  onChange={(e) => setPoForm((p) => ({ ...p, unit: e.target.value }))}
                >
                  <option value="ton">Ton</option>
                  <option value="kg">Kg</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className="g-3 mt-1">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Price per Unit (₹)</Form.Label>
                <Form.Control
                  type="number"
                  value={poForm.pricePerUnit}
                  onChange={(e) => setPoForm((p) => ({ ...p, pricePerUnit: e.target.value }))}
                  placeholder="25000"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Paid Amount (₹)</Form.Label>
                <Form.Control
                  type="number"
                  value={poForm.paidAmount}
                  onChange={(e) => setPoForm((p) => ({ ...p, paidAmount: e.target.value }))}
                  placeholder="0"
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mt-3">
            <Form.Label>Total Amount (₹)</Form.Label>
            <Form.Control
              value={`₹${poTotalAmount.toLocaleString("en-IN")}`}
              readOnly
            />
            <Form.Text className="text-muted">
              Total Quantity × Price per Unit - Paid Amount
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPoModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={savePo} disabled={!poSupplier || actionLoading === poSupplier?._id}>
            {actionLoading === poSupplier?._id ? "Saving..." : poMode === "edit" ? "Update PO" : "Create PO"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminSupplierList;
